
import Session from "@/model/Session";
import store from "@/store";
import SessionsModule from "@/store/modules/Sessions";
import Vue from "vue";
import Component from 'vue-class-component';
import { Prop, Watch } from "vue-property-decorator";
import { getModule } from "vuex-module-decorators";
import SessionListElement from "@/model/SessionListElement";

const sessionsModule = getModule(SessionsModule, store);

@Component
export default class Downloads extends Vue {
    public agreeDownloadWM = false;
    public buyThenSelect = false;
    public buyThenSend = false;
    public loading = false;
    public downloadWarningDismissed = false;
    private pendingDownloadSelection = false;
    private hydratingState = false;

    @Prop({ required: true })
    session!: Session|SessionListElement;

    @Prop({ default: false })
    isActive!: boolean;

    @Prop({ default: false })
    dialogVisible!: boolean;

    get finalizationEnabled() {
        return this.session.finalizationEnabled;
    }

    get downloadEnabled() {
        return this.session.downloadEnabled;
    }

    get shouldShowDownloadWarning() {
        return this.agreeDownloadWM && !this.downloadWarningDismissed;
    }

    created() {
        this.syncFromSession();
    }

    @Watch('session.downloadEnabled')
    public onSessionDownloadEnabledChanged() {
        this.syncFromSession();
    }

    @Watch('session.finalizationEnabled')
    public onSessionFinalizationEnabledChanged() {
        this.syncFromSession();
    }

    @Watch('isActive')
    public onIsActiveChange(active: boolean) {
        if (!active) {
            this.ensureValidDownloadState();
        }
    }

    @Watch('dialogVisible')
    public onDialogVisibleChange(visible: boolean) {
        if (!visible) {
            this.ensureValidDownloadState();
        }
    }

    @Watch('agreeDownloadWM')
    public onAgreeDownloadWMChange() {
        if (this.hydratingState) return;

        // Only after explicit OFF -> ON by the user we wait for manual option choice.
        if (this.agreeDownloadWM && !this.session.downloadEnabled) {
            this.pendingDownloadSelection = true;
            this.buyThenSelect = false;
            this.buyThenSend = false;
        }

        if (!this.agreeDownloadWM) {
            this.pendingDownloadSelection = false;
        }

        if (this.session.downloadEnabled === this.agreeDownloadWM) return;

        if (this.agreeDownloadWM) {
            sessionsModule.enableDownload(this.session.id);
        } else {
            sessionsModule.disableDownload(this.session.id);
        }
    }

    public beforeDestroy() {
        this.ensureValidDownloadState();
    }

    public onBuyThenSelectChange(v: boolean) {
        if (!v) return;
        this.buyThenSend = false;
        this.pendingDownloadSelection = false;
        this.finalizationStatusChanged();
        (this as any).$vToastify.info({ title: 'Udało się!', body: 'Pobieranie włączone' });
    }

    public onBuyThenSendChange(v: boolean) {
        if (!v) return;
        this.buyThenSelect = false;
        this.pendingDownloadSelection = false;
        this.finalizationStatusChanged();
        (this as any).$vToastify.info({ title: 'Udało się!', body: 'Pobieranie włączone' });
    }

    public dismissDownloadWarning() {
        this.downloadWarningDismissed = true;
    }

    private ensureValidDownloadState() {
        if (!this.pendingDownloadSelection) return;
        if (!this.agreeDownloadWM) return;
        if (this.buyThenSelect || this.buyThenSend) return;
        this.agreeDownloadWM = false;
    }

    private syncFromSession() {
        this.hydratingState = true;
        this.agreeDownloadWM = this.downloadEnabled;

        // Keep both options off while waiting for first explicit choice after OFF -> ON.
        if (this.pendingDownloadSelection && this.downloadEnabled) {
            this.buyThenSelect = false;
            this.buyThenSend = false;
            this.hydratingState = false;
            return;
        }

        if (this.downloadEnabled) {
            this.buyThenSelect = this.finalizationEnabled;
            this.buyThenSend = !this.finalizationEnabled;
        } else {
            this.buyThenSelect = false;
            this.buyThenSend = false;
        }

        this.pendingDownloadSelection = false;
        this.hydratingState = false;
    }

    public async finalizationStatusChanged() {
        this.loading = true;
        try {
            if (this.buyThenSelect && !this.finalizationEnabled) {
                await sessionsModule.enableFinalization(this.session.id);
            } else if (this.buyThenSend && this.finalizationEnabled) {
                await sessionsModule.disableFinalization(this.session.id);
            }
        } finally {
            this.loading = false;
        }
    }
}
